/**
 * Pre-Order Warning Popup Handler
 *
 * This script handles the pre-order functionality:
 * - Detects products with pre-order tags
 * - Replaces the Add to Cart button with a Pre-Order button
 * - Shows a warning popup when the Pre-Order button is clicked
 * - Simulates a click on the original Add to Cart button when confirmed
 */

class PreOrderWarning {
  constructor() {
    this.init()
  }

  init() {
    // Initialize once DOM is fully loaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup())
    } else {
      this.setup()
    }
  }

  setup() {
    // Find the popup element
    this.popup = document.querySelector("[data-warning-popup]")
    if (!this.popup) {
      console.log("Warning popup element not found, skipping initialization")
      return
    }

    // Get popup elements
    this.wrapper = this.popup.querySelector(".popup-section__wrapper")
    this.closeButtons = this.popup.querySelectorAll("[data-warning-close]")
    this.continueButton = this.popup.querySelector("[data-warning-continue]")
    this.overlay = this.popup.querySelector(".popup-section__overlay")

    // Debug
    console.log("Pre-order popup initialized with elements:", {
      popup: this.popup,
      wrapper: this.wrapper,
      closeButtons: this.closeButtons,
      continueButton: this.continueButton
    })

    // Add event listeners to popup buttons
    this.addPopupEventListeners()

    // Check if current product is a pre-order product and setup buttons
    this.setupPreOrderButtons()
  }

  addPopupEventListeners() {
    // Close button(s)
    this.closeButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault()
        this.hidePopup()
      })
    })

    // Continue button - this will trigger the actual add to cart
    if (this.continueButton) {
      this.continueButton.addEventListener("click", (e) => {
        e.preventDefault()
        this.confirmAddToCart()
      })
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener("click", () => this.hidePopup())
    }

    // Listen for ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isPopupVisible()) {
        this.hidePopup()
      }
    })
  }

  /**
   * Check if the current product is a pre-order product
   * and modify the add to cart buttons/forms accordingly
   */
  setupPreOrderButtons() {
    // Check for product pages
    this.setupProductPageButtons()

    // Check for quickshop popups (present on collection pages)
    this.setupQuickshopButtons()

    // Set up mutation observer to detect dynamically added quickshop popups
    this.observeQuickshopPopups()
  }

  /**
   * Set up pre-order buttons on product pages
   */
  setupProductPageButtons() {
    // Only proceed if we're on a product page with product information
    // if (!this.isProductPage()) return

    // Check product availability from preorders-data JSON
    let available = true
    const preordersData = this.findNearestPreordersDataScript(document.body)
    if (preordersData) {
      try {
        const data = JSON.parse(preordersData.textContent)
        if (
          data.current_product &&
          typeof data.current_product.available !== "undefined"
        ) {
          available = !!data.current_product.available
        }
      } catch (e) {
        /* ignore parse errors */
      }
    }
    if (!available) return

    // Find all add to cart forms and buttons
    const forms = document.querySelectorAll('form[action*="/cart/add"]')

    forms.forEach((form) => {
      this.convertFormToPreOrder(form)
    })
  }

  /**
   * Set up pre-order buttons in quickshop popups
   */
  setupQuickshopButtons() {
    // Find all quickshop containers
    const quickshopContainers = document.querySelectorAll(
      ".product-quickshop__container"
    )

    quickshopContainers.forEach((container) => {
      this.processQuickshopContainer(container)
    })
  }

  /**
   * Process a quickshop container to check if it contains a pre-order product
   * @param {Element} container - The quickshop container element
   */
  processQuickshopContainer(container) {
    // Check if this is a pre-order product based on the product title or URL
    const isPreOrder = this.isQuickshopProductPreOrder(container)
    const form = container.querySelector('form[action*="/cart/add"]')
    if (!form) return

    // Check product availability from preorders-data JSON
    let available = true
    const preordersData = this.findNearestPreordersDataScript(form)
    if (preordersData) {
      try {
        const data = JSON.parse(preordersData.textContent)
        if (
          data.current_product &&
          typeof data.current_product.available !== "undefined"
        ) {
          available = !!data.current_product.available
        }
      } catch (e) {
        /* ignore parse errors */
      }
    }
    if (!available) return

    if (isPreOrder) {
      // Pre-order logic: hide original button, add new button, show popup on click
      this.convertFormToPreOrder(form)
    } else {
      // Non-preorder logic: add button if not already present, HIDE original, simulate click on hidden button if allowed, else show popup
      if (form.querySelector("[data-pre-order-button]")) return
      const addToCartBtn = form.querySelector(
        'button[name="add"], input[name="add"], button.add-to-cart, button.product-form--add-to-cart'
      )
      if (!addToCartBtn) return
      addToCartBtn.style.display = "none"
      const newBtn = this.createPreOrderButton()
      newBtn.textContent = "ADD TO CART"
      newBtn.addEventListener("click", (e) => {
        e.preventDefault()
        // Check cart conditions before submitting
        this.checkCartConditions(form).then((shouldShowPopup) => {
          if (!shouldShowPopup) {
            // Allowed: simulate click on hidden button (native handlers will run)
            addToCartBtn.click()
          } else {
            // Not allowed: show popup
            this.activeForm = form
            this.originalAddToCartButton = addToCartBtn
            this.showPopup()
          }
        })
      })
      addToCartBtn.insertAdjacentElement("afterend", newBtn)
    }
  }

  /**
   * Check if a quickshop product is a pre-order product
   * @param {Element} container - The quickshop container element
   * @returns {boolean} - Whether the product is a pre-order product
   */
  isQuickshopProductPreOrder(container) {
    // Check product title for pre-order indicators
    const titleElement = container.querySelector(".product__title")
    if (titleElement && titleElement.textContent) {
      const titleText = titleElement.textContent.toLowerCase().trim()
      if (
        titleText.includes("pre-order") ||
        titleText.includes("preorder") ||
        titleText.includes("pre order")
      ) {
        return true
      }
    }

    // Check product URL for pre-order indicators
    const productUrlElement = container.querySelector("[data-product-url]")
    if (productUrlElement) {
      const productUrl =
        productUrlElement.getAttribute("data-product-url") || ""
      if (
        productUrl.toLowerCase().includes("pre-order") ||
        productUrl.toLowerCase().includes("preorder")
      ) {
        return true
      }
    }

    return false
  }

  /**
   * Convert a form to use the pre-order button instead of add to cart
   * @param {Element} form - The form element to convert
   */
  convertFormToPreOrder(form) {
    // Mark the form as a pre-order form
    form.setAttribute("data-is-pre-order", "true")

    // Find the add to cart button within the form
    const addToCartBtn = form.querySelector(
      'button[name="add"], input[name="add"], button.add-to-cart, button.product-form--add-to-cart'
    )
    if (!addToCartBtn) return

    // Skip if this button has already been processed
    if (form.querySelector("[data-pre-order-button]")) return

    // Hide the original add to cart button with CSS
    addToCartBtn.classList.add("is-pre-order")

    // Create and insert the pre-order button
    const preOrderBtn = this.createPreOrderButton()
    addToCartBtn.insertAdjacentElement("afterend", preOrderBtn)

    // Store reference to the original add to cart button
    preOrderBtn.dataset.originalButton = true

    // When pre-order button is clicked, show the warning popup
    preOrderBtn.addEventListener("click", (e) => {
      e.preventDefault()

      // Store references to the form and original button for later use
      this.activeForm = form
      this.originalAddToCartButton = addToCartBtn

      // Show the warning popup
      this.showPopup()
    })
  }

  /**
   * Set up a mutation observer to detect dynamically added quickshop popups
   */
  observeQuickshopPopups() {
    // Create a new MutationObserver to watch for changes to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check for added nodes
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          // Check each added node
          mutation.addedNodes.forEach((node) => {
            // Check if the node is a quickshop container or contains one
            if (node.nodeType === 1) {
              // ELEMENT_NODE
              if (
                node.classList &&
                node.classList.contains("product-quickshop__container")
              ) {
                // Process this new quickshop container
                this.processQuickshopContainer(node)
              } else {
                // Check for quickshop containers within this node
                const containers = node.querySelectorAll(
                  ".product-quickshop__container"
                )
                containers.forEach((container) => {
                  this.processQuickshopContainer(container)
                })
              }
            }
          })
        }
      })
    })

    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true })

    // Store the observer reference for potential cleanup later
    this.quickshopObserver = observer
  }

  /**
   * Create a pre-order button element with text from theme settings
   */
  createPreOrderButton() {
    // Create button element
    const button = document.createElement("button")
    button.type = "button"
    button.className = "pre-order-button"

    // Set button text based on product type
    let buttonText = this.isPreOrderProduct() ? "PRE ORDER" : "ADD TO CART"
    button.textContent = buttonText
    button.setAttribute("data-pre-order-button", "")

    // Loader logic: show loader for 1s on click, then restore text
    button.addEventListener("click", (e) => {
      // Only add loader if not already present
      if (button.querySelector(".loading-icon")) return
      const originalText = button.textContent
      button.textContent = ""
      // Add animated three dots loader (inline CSS)
      const loader = document.createElement("span")
      loader.className = "inline-ellipsis-loader"
      loader.innerHTML = `
        <style>
          .inline-ellipsis-loader {
            display: inline-block;
            width: 32px;
            height: 16px;
            vertical-align: middle;
            position: relative;
          }
          .inline-ellipsis-loader span {
            display: inline-block;
            width: 6px;
            height: 6px;
            background: currentColor;
            border-radius: 50%;
            margin: 0 2px;
            opacity: 0.7;
            animation: ellipsis-bounce 1s infinite both;
          }
          .inline-ellipsis-loader span:nth-child(2) { animation-delay: 0.2s; }
          .inline-ellipsis-loader span:nth-child(3) { animation-delay: 0.4s; }
          @keyframes ellipsis-bounce {
            0%, 80%, 100% { transform: scale(1); opacity: 0.7; }
            40% { transform: scale(1.3); opacity: 1; }
          }
        </style>
        <span></span><span></span><span></span>
      `
      button.appendChild(loader)
      // Disable button to prevent double-click
      button.disabled = true
      setTimeout(() => {
        // Remove loader and restore text
        if (button.contains(loader)) button.removeChild(loader)
        button.textContent = originalText
        button.disabled = false
      }, 2500)
    })

    return button
  }

  /**
   * Check if the current page is a product page
   */
  isProductPage() {
    const preordersData = document.getElementById("preorders-data")
    if (!preordersData) return false

    try {
      const data = JSON.parse(preordersData.textContent)
      return data.page_type === "product" && data.current_product
    } catch (error) {
      console.error("Error parsing preorders data:", error)
      return false
    }
  }

  /**
   * Check if the current product is a pre-order product
   * based on its tags
   */
  // Helper: Find nearest preorders-data script above a given element
  findNearestPreordersDataScript(element) {
    let el = element
    while (el) {
      // Look for previous siblings up the DOM tree
      let sibling = el.previousElementSibling
      while (sibling) {
        if (
          sibling.tagName === "SCRIPT" &&
          sibling.type === "application/json" &&
          sibling.id === "preorders-data"
        ) {
          return sibling
        }
        sibling = sibling.previousElementSibling
      }
      el = el.parentElement
    }
    // Fallback: global (legacy)
    return document.getElementById("preorders-data")
  }

  isPreOrderProduct(buttonOrForm) {
    // Accepts a button, form, or any element inside the product form
    const preordersData = this.findNearestPreordersDataScript(
      buttonOrForm || document.body
    )
    if (!preordersData) return false
    try {
      const data = JSON.parse(preordersData.textContent)
      if (data.current_product && data.current_product.tags) {
        const tags = data.current_product.tags
        const isPreOrder = tags.some((tag) => {
          const tagLower = tag.toLowerCase()
          return (
            tagLower.includes("pre-order") ||
            tagLower.includes("preorder") ||
            tagLower.includes("pre_order")
          )
        })
        console.log("Pre-order tags check:", isPreOrder, "Tags:", tags)
        return isPreOrder
      }
    } catch (error) {
      console.error("Error checking pre-order status:", error)
    }
    return false
  }

  /**
   * Show the warning popup with cart condition checking
   */
  showPopup() {
    if (this.popup && this.wrapper) {
      // Check cart conditions before showing popup
      this.checkCartConditions().then((shouldShowPopup) => {
        if (!shouldShowPopup) {
          // Skip popup and directly add to cart
          console.log("Skipping pre-order popup due to cart conditions")
          this.confirmAddToCart()
          return
        }

        // Get the current product title
        let productTitle = "this product"

        try {
          const preordersData = document.getElementById("preorders-data")
          if (preordersData) {
            const data = JSON.parse(preordersData.textContent)
            if (data.current_product && data.current_product.title) {
              productTitle = data.current_product.title
            }
          }
        } catch (error) {
          console.error("Error getting product title:", error)
        }

        // Update any placeholder in the message
        const messageEl = this.popup.querySelector(".popup-section__message")
        if (messageEl) {
          const originalMessage = messageEl.innerHTML
          const updatedMessage = originalMessage
            .replace(/{product}/g, productTitle)
            .replace(/{product2}/g, productTitle)
          messageEl.innerHTML = updatedMessage
        }

        // Show the popup
        this.popup.style.display = "flex"
        this.wrapper.style.display = "flex"
        document.body.classList.add("popup-active")
        console.log("Showing pre-order warning popup for:", productTitle)
      })
    }
  }

  /**
   * Check cart conditions to determine if popup should be shown
   * @returns {Promise<boolean>} True if popup should be shown, false if it should be skipped
   */
  async checkCartConditions(buttonOrForm) {
    try {
      // Find the nearest preorders-data script for this button/form
      const preordersData = this.findNearestPreordersDataScript(
        buttonOrForm || document.body
      )
      if (preordersData) {
        try {
          const data = JSON.parse(preordersData.textContent)
          if (data.current_product && data.current_product.collections) {
            const collections = data.current_product.collections
            if (collections.includes("custom-hem-service-1")) {
              console.log(
                "Product is in custom-hem-service-1 collection, skipping popup"
              )
              return false
            }
          }
        } catch (e) {
          /* ignore, fallback to normal logic */
        }
      }
      // Determine if the current product is pre-order
      let addingIsPreOrder = false
      if (typeof this.isPreOrderProduct === "function") {
        addingIsPreOrder = this.isPreOrderProduct(buttonOrForm)
      }

      // Fetch current cart data
      const response = await fetch("/cart.js")
      if (!response.ok) {
        console.error("Failed to fetch cart data")
        return true // Show popup if we can't check cart
      }

      const cart = await response.json()

      // If cart is empty, don't show popup
      if (!cart.items || cart.items.length === 0) {
        console.log("Cart is empty, skipping popup")
        return false
      }

      if (addingIsPreOrder) {
        // If adding a pre-order product, show popup if cart has any regular product
        for (const item of cart.items) {
          if (this.isWarrantyProduct(item)) continue
          if (!this.isPreOrderMetafieldProduct(item)) {
            console.log("Found regular item in cart:", item.title)
            return true // Show popup
          }
        }
      } else {
        // If adding a regular product, show popup if cart has any pre-order product
        for (const item of cart.items) {
          if (this.isWarrantyProduct(item)) continue
          if (this.isPreOrderMetafieldProduct(item)) {
            console.log("Found pre-order item in cart:", item.title)
            return true // Show popup
          }
        }
      }

      // No mixing found, do not show popup
      return false
    } catch (error) {
      console.error("Error checking cart conditions:", error)
      return true // Show popup if we encounter any errors
    }
  }

  /**
   * Check if an item is the Brave Star Shipping Warranty product
   * @param {Object} item - Cart item
   * @returns {boolean} True if item is the warranty product
   */
  isWarrantyProduct(item) {
    if (!item || !item.handle) return false

    return (
      item.handle.includes("brave-star-shipping-warranty") ||
      (item.title && item.title.toLowerCase().includes("shipping warranty"))
    )
  }

  /**
   * Check if an item is a pre-order product with product.label metafield
   * @param {Object} item - Cart item
   * @returns {boolean} True if item is a pre-order product with metafield
   */
  isPreOrderMetafieldProduct(item) {
    if (!item) return false

    // Check if the item has properties that might contain metafield data
    if (item.properties) {
      // Iterate through properties which may contain the metafield value
      for (const key in item.properties) {
        // Check if property value contains 'pre order'
        const value = item.properties[key]
        if (
          value &&
          typeof value === "string" &&
          value.toLowerCase().includes("pre order")
        ) {
          console.log("Found pre-order metafield in properties:", key, value)
          return true
        }
      }
    }

    // Check product title for pre-order indicator as fallback
    if (item.title && item.title.toLowerCase().includes("pre order")) {
      console.log("Found pre-order in title:", item.title)
      return true
    }

    return false
  }

  /**
   * Hide the warning popup
   */
  hidePopup() {
    if (this.popup && this.wrapper) {
      this.popup.style.display = "none"
      this.wrapper.style.display = "none"
      document.body.classList.remove("popup-active")
      console.log("Hiding pre-order warning popup")
    }
  }

  /**
   * Check if the popup is currently visible
   */
  isPopupVisible() {
    return this.wrapper && this.wrapper.style.display === "flex"
  }

  /**
   * Confirm the add to cart action when the continue button is clicked
   * This simulates a click on the original add to cart button
   */
  confirmAddToCart() {
    console.log("Confirming add to cart for pre-order product")

    // Hide the popup first to prevent any layout issues
    this.hidePopup()

    if (this.originalAddToCartButton) {
      try {
        // First, try to programmatically trigger a native click event
        console.log("Triggering native click event on original button")
        const clickEvent = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window
        })

        // Prevent the original button from redirecting
        const preventRedirect = (e) => {
          e.preventDefault()
          this.originalAddToCartButton.removeEventListener(
            "click",
            preventRedirect
          )
        }

        // Remove is-pre-order class temporarily to make it clickable
        this.originalAddToCartButton.classList.remove("is-pre-order")

        // Dispatch the click event
        this.originalAddToCartButton.dispatchEvent(clickEvent)

        // Add the class back after a small delay
        setTimeout(() => {
          this.originalAddToCartButton.classList.add("is-pre-order")
        }, 100)
      } catch (error) {
        console.error("Error triggering click event:", error)

        // Fallback: Submit the form directly
        if (this.activeForm) {
          console.log("Using fallback: direct form submission")
          try {
            // Temporarily store any form attributes we need to preserve
            const originalAction = this.activeForm.action
            const originalMethod = this.activeForm.method

            // Submit the form
            this.activeForm.submit()
          } catch (formError) {
            console.error("Failed to submit form:", formError)
          }
        }
      }
    } else {
      console.error("Original add to cart button not found")

      // Last resort: try to find the form again and submit it
      if (this.activeForm) {
        console.log("Last resort: direct form submission")
        this.activeForm.submit()
      } else {
        console.error("No form reference available for submission")
      }
    }
  }
}

// Initialize the pre-order warning popup
document.addEventListener("DOMContentLoaded", () => {
  console.log("Initializing pre-order warning popup")
  new PreOrderWarning()
})

// Also initialize on page loads via Turbo or PJAX if applicable
if (typeof document.addEventListener === "function") {
  document.addEventListener("page:load", () => new PreOrderWarning())
  document.addEventListener("turbo:load", () => new PreOrderWarning())
}
